import './App.css';
import { useEffect } from 'react';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import Home from './Home';

// Third-party services that can be listed with ?services=play+admob (see README).
// Without the parameter, all of them are listed (the original policy).
const SERVICES = {
  play: { name: 'Google Play Services', url: 'https://www.google.com/policies/privacy/' },
  admob: { name: 'AdMob', url: 'https://support.google.com/admob/answer/6128543?hl=en' },
  analytics: { name: 'Google Analytics for Firebase', url: 'https://firebase.google.com/policies/analytics' },
  crashlytics: { name: 'Firebase Crashlytics', url: 'https://firebase.google.com/support/privacy/' },
  openrouter: { name: 'OpenRouter', url: 'https://openrouter.ai/privacy' },
};

// Meta (Facebook) ad measurement is listed on every policy, whatever ?services= says.
const META_PRIVACY_URL = 'https://www.facebook.com/privacy/policy/';

const TRUE_VALUES = ['1', 'true', 'yes', 'on'];
const FALSE_VALUES = ['0', 'false', 'no', 'off'];

function PrivacyPolicyContent() {
  const [searchParams] = useSearchParams();

  // Get values from URL parameters with defaults
  const appName = searchParams.get('name') || 'Money Quiz';
  const email = searchParams.get('email') || 'name6007@gmail.com';
  const developer = searchParams.get('developer') || 'MS Developers';
  const company = searchParams.get('company') || 'MS Technologies';
  const effectiveDate = searchParams.get('effectiveDate') || searchParams.get('date') || '2022-07-23';

  // Optional on/off switches (1/0, true/false, yes/no). Defaults keep the original policy.
  const flag = (key, fallback) => {
    const value = (searchParams.get(key) || '').toLowerCase();
    if (TRUE_VALUES.includes(value)) return true;
    if (FALSE_VALUES.includes(value)) return false;
    return fallback;
  };
  const showAds = flag('ads', false);
  const showNotifications = flag('notifications', false);
  const hasAccount = flag('account', true);
  const showAi = flag('ai', true);
  const showTrademarks = flag('trademarks', true);

  // ?services=play+admob (separated by +, space, comma or |). Unknown names are ignored.
  const servicesParam = searchParams.get('services');
  const serviceKeys = servicesParam
    ? servicesParam.toLowerCase().split(/[\s,|]+/).filter((key) => SERVICES[key])
    : Object.keys(SERVICES);
  const usesCrashReporting = serviceKeys.includes('crashlytics');

  // Update document title
  useEffect(() => {
    document.title = `${appName} Privacy Policy`;
  }, [appName]);

  // Links like ...#data-deletion: scroll once the policy has rendered.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  const sections = [
    {
      title: 'Introduction',
      body: (
        <>
          <p>
            {developer} built the {appName} app as a Free app. This SERVICE is provided by {company} at no cost and is intended for use as is.
          </p>
          <p>
            This page is used to inform visitors regarding our policies with the collection, use, and disclosure of Personal Information if anyone decided to use our Service.
          </p>
          <p>
            If you choose to use our Service, then you agree to the collection and use of information in relation to this policy. The Personal Information that we collect is used for providing and improving the Service. We will not use or share your information with anyone except as described in this Privacy Policy.
          </p>
          <p>
            The terms used in this Privacy Policy have the same meanings as in our Terms and Conditions, which are accessible at {appName} unless otherwise defined in this Privacy Policy.
          </p>
        </>
      ),
    },
    {
      title: 'Information Collection and Use',
      body: (
        <>
          <p>
            For a better experience while using our Service, we may require you to provide us with certain personally identifiable information. However, we want to inform you that we do not collect any personal data while using the app. The information that we request will be retained on your device and is not collected by us in any way.
          </p>
          <p>
            The app does use third-party services that may collect information used to identify you.
          </p>
          <p>
            <strong>Link to the privacy policy of third-party service providers used by the app:</strong>
          </p>
          <ul>
            {serviceKeys.map((key) => (
              <li key={key}><a href={SERVICES[key].url} target="_blank" rel="noopener noreferrer">{SERVICES[key].name}</a></li>
            ))}
            <li><a href={META_PRIVACY_URL} target="_blank" rel="noopener noreferrer">Meta (Facebook) App Events</a></li>
          </ul>
        </>
      ),
    },
    showAds && {
      id: 'advertising',
      title: 'Advertising (Google AdMob)',
      body: (
        <>
          <p>
            {appName} is free and shows ads provided by Google AdMob. To show and measure ads, the Google Mobile Ads SDK may automatically collect and share the following data:
          </p>
          <ul>
            <li><strong>Approximate location</strong>, estimated from your device's IP address;</li>
            <li><strong>App interactions</strong>, such as app launches, taps and ad views;</li>
            <li><strong>Diagnostics</strong>, such as app and ad performance data;</li>
            <li><strong>Device or other identifiers</strong>, such as the Android Advertising ID and app set ID.</li>
          </ul>
          <p>
            This data is used for advertising, analytics and fraud prevention, and is encrypted in transit. You can learn more in <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">How Google uses information from sites or apps that use its services</a>.
          </p>
          <p>
            If you are in the European Economic Area, the United Kingdom or Switzerland, the app asks for your consent through Google's consent form before personalized ads are shown. Where required, you can change your choice at any time from the Privacy options in the app's settings.
          </p>
          <p>
            You can reset or delete your Advertising ID, or opt out of personalized ads, in your device settings (for example Settings → Privacy → Ads; the wording may vary by device).
          </p>
        </>
      ),
    },
    {
      id: 'meta',
      title: 'Ad Measurement (Meta)',
      body: (
        <>
          <p>
            We may advertise {appName} on Facebook and Instagram. To measure those ads, the app may use the Meta (Facebook) SDK, which can collect and share the following data with Meta Platforms:
          </p>
          <ul>
            <li><strong>Device or other identifiers</strong>, such as the Android Advertising ID;</li>
            <li><strong>App interactions</strong>, such as installing and opening the app and progress in it (for example, levels finished);</li>
            <li><strong>Approximate location</strong>, estimated from your device's IP address, and basic device information such as the model and operating system version.</li>
          </ul>
          <p>
            This data is used for advertising and analytics: to count installs that came from our ads and to show our ads to people who are more likely to enjoy the app. It is encrypted in transit. Where the law requires consent (for example in the European Economic Area, the United Kingdom or Switzerland), the app does not send this data to Meta without it.
          </p>
          <p>
            You can reset or delete your Advertising ID in your device settings, and control the ads you see on Facebook and Instagram in your <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer">Meta ad preferences</a>. Learn more in the <a href={META_PRIVACY_URL} target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a>.
          </p>
        </>
      ),
    },
    showNotifications && {
      id: 'notifications',
      title: 'Notifications',
      body: (
        <p>
          If you allow notifications, the app schedules reminders locally on your device (for example, when a reward is ready). No personal data is sent to us to do this. You can turn reminders off in the app's settings or in your device's notification settings.
        </p>
      ),
    },
    {
      title: 'Log Data',
      body: usesCrashReporting ? (
        <p>
          We want to inform you that whenever you use our Service, in a case of an error in the app, we collect data and information (through third-party products) on your phone called Log Data. This Log Data may include information such as your device Internet Protocol ("IP") address, device name, operating system version, the configuration of the app when utilizing our Service, the time and date of your use of the Service, and other statistics.
        </p>
      ) : (
        <p>
          We do not collect log data ourselves. Third-party services used by the app may automatically collect information such as your device Internet Protocol ("IP") address, device model, operating system version, the time and date of your use of the Service, and other statistics, as described in their privacy policies listed above.
        </p>
      ),
    },
    {
      title: 'Cookies',
      body: (
        <>
          <p>
            Cookies are files with a small amount of data that are commonly used as anonymous unique identifiers. These are sent to your browser from the websites that you visit and are stored on your device's internal memory.
          </p>
          <p>
            This Service does not use these "cookies" explicitly. However, the app may use third-party code and libraries that use "cookies" to collect information and improve their services. You have the option to either accept or refuse these cookies and know when a cookie is being sent to your device. If you choose to refuse our cookies, you may not be able to use some portions of this Service.
          </p>
        </>
      ),
    },
    {
      title: 'Service Providers',
      body: (
        <>
          <p>
            We may employ third-party companies and individuals due to the following reasons:
          </p>
          <ul>
            <li>To facilitate our Service;</li>
            <li>To provide the Service on our behalf;</li>
            <li>To perform Service-related services; or</li>
            <li>To assist us in analyzing how our Service is used.</li>
          </ul>
          <p>
            We want to inform users of this Service that these third parties have access to their Personal Information. The reason is to perform the tasks assigned to them on our behalf. However, they are obligated not to disclose or use the information for any other purpose.
          </p>
        </>
      ),
    },
    {
      title: 'Security',
      body: (
        <p>
          We value your trust in providing us your Personal Information, thus we are striving to use commercially acceptable means of protecting it. But remember that no method of transmission over the internet, or method of electronic storage is 100% secure and reliable, and we cannot guarantee its absolute security.
        </p>
      ),
    },
    {
      title: 'Links to Other Sites',
      body: (
        <p>
          This Service may contain links to other sites. If you click on a third-party link, you will be directed to that site. Note that these external sites are not operated by us. Therefore, we strongly advise you to review the Privacy Policy of these websites. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
        </p>
      ),
    },
    {
      title: "Children's Privacy",
      body: (
        <p>
          These Services do not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13 years of age. In the case we discover that a child under 13 has provided us with personal information, we immediately delete this from our servers. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us so that we will be able to do the necessary actions.
        </p>
      ),
    },
    {
      title: 'Changes to This Privacy Policy',
      body: (
        <>
          <p>
            We may update our Privacy Policy from time to time. Thus, you are advised to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page.
          </p>
          <p>
            This policy is effective as of {effectiveDate}.
          </p>
        </>
      ),
    },
    {
      id: 'contact',
      title: 'Contact Us',
      body: (
        <p>
          If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at <a href={`mailto:${email}`}>{email}</a>.
        </p>
      ),
    },
    showAi && {
      title: 'AI Features and Data Processing',
      body: (
        <>
          <p>
            Our Service utilizes artificial intelligence to generate content. When you use these features, your AI prompts and attached document text are sent to and processed by a third-party AI provider (OpenRouter). By using these features, you consent to this data being shared with OpenRouter for the purpose of generating the requested content.
          </p>
          <p>
            We are committed to providing a safe environment. If you encounter any offensive or inappropriate AI-generated content, you can report or flag it directly within the app or by contacting us at <a href={`mailto:${email}`}>{email}</a>.
          </p>
        </>
      ),
    },
    showTrademarks && {
      title: 'Third-Party Trademarks',
      body: (
        <p>
          Gmail, Outlook, and Yahoo are trademarks of their respective owners. Their use in this app or related materials does not imply any affiliation with or endorsement by them.
        </p>
      ),
    },
    hasAccount
      ? {
          id: 'data-deletion',
          title: 'Account and Data Deletion',
          body: (
            <>
              <p>
                We respect your right to control your data. If you wish to delete your account and all associated data (including quiz history and profile information), please contact us via email.
              </p>
              <p>
                <strong>How to request deletion:</strong>
              </p>
              <p>
                Email: <a href={`mailto:${email}`}>{email}</a><br />
                Subject: Account Deletion Request<br />
                Process: Upon receiving your request, we will verify your identity and permanently delete your account and data from our servers within 30 days.
              </p>
            </>
          ),
        }
      : {
          id: 'data-deletion',
          title: 'Data Retention and Deletion',
          body: (
            <>
              <p>
                {appName} does not use accounts. Your progress and settings are stored only on your device and are never sent to us. They are deleted when you uninstall the app or clear its data (Android Settings → Apps → select the app → Storage → Clear data).
              </p>
              <p>
                Data collected by third-party services{showAds ? ' (such as Google AdMob and Meta)' : ' (such as Meta)'} is kept by those services under their own retention policies. You can reset or delete your Advertising ID in your device settings, manage your Google data at <a href="https://myaccount.google.com/data-and-privacy" target="_blank" rel="noopener noreferrer">myaccount.google.com</a>, and manage your Meta ad settings at <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer">facebook.com/adpreferences</a>.
              </p>
              <p>
                <strong>How to request deletion:</strong>
              </p>
              <p>
                Email: <a href={`mailto:${email}`}>{email}</a><br />
                Subject: Data Deletion Request<br />
                Process: We will reply within 30 days and help with any data we or our service providers hold about you.
              </p>
            </>
          ),
        },
  ].filter(Boolean);

  return (
    <div>
      <header className="App-header">
        <h2>{appName} Privacy Policy</h2>
      </header>
      <div className="container mt-3" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>

        <p><strong>Effective Date:</strong> {effectiveDate}</p>

        {sections.map((section, index) => (
          <section key={section.title}>
            <h3 id={section.id}>{index + 1}. {section.title}</h3>
            {section.body}
          </section>
        ))}

      </div>
    </div>
  );
}

// Policy links always carry URL parameters (?name=…&effectiveDate=…), or go straight to a section
// (#data-deletion): those show the policy exactly as before. The bare site shows the home page.
const POLICY_SECTIONS = ['data-deletion', 'advertising', 'meta', 'notifications', 'contact'];

function Root() {
  const { search, hash } = useLocation();
  const hasParams = [...new URLSearchParams(search).keys()].length > 0;
  return hasParams || POLICY_SECTIONS.includes(hash.slice(1)) ? <PrivacyPolicyContent /> : <Home />;
}

// /app-ads.txt is the plain-text file public/app-ads.txt, served as it is (AdMob's crawler
// doesn't run JavaScript, so it can't come from a React route).
function App() {
  return (
    <div >
      <Routes>
        <Route path="/" element={<Root />} />
      </Routes>

    </div>

  );
}

export default App;
